import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle, ChevronDown, Search, MessageSquare, Swords, GraduationCap, Target, ShieldAlert,
} from "lucide-react";
import api from "@/lib/api";

const track = (event, data) => api.post("/daily/event", { event, data }).catch(() => {});

// Reusable "Why am I seeing this?" — deterministic evidence + personal relevance.
export default function WhyThis({ card, assist = "SUPPORTED", compact = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!card) return null;
  const rg = card.risk_geometry_summary || {};
  const mat = (card.condition_maturity || {}).state;
  const sym = card.symbol;

  const toggle = () => {
    const n = !open;
    setOpen(n);
    if (n) track("why_this_opened", { symbol: sym, situation: card.situation_type });
  };

  return (
    <div className="mt-2" data-testid={`why-this-${sym}`}>
      <button data-testid={`why-this-toggle-${sym}`} onClick={toggle}
        className="flex items-center gap-1.5 text-[12px] text-tc-coach hover:text-tc-coach/80 transition-colors">
        <HelpCircle className="w-3.5 h-3.5" /> Why am I seeing this?
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2 animate-fade-up">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
            <Field label="Situation" value={card.situation_type} />
            <Field label="Condition" value={mat} />
            <Field label="Lean" value={card.lean} />
            <Field label="Actionability" value={card.actionability} />
          </div>
          {card.supporting_evidence?.length > 0 && (
            <Group label="Supporting evidence" items={card.supporting_evidence} cls="text-tc-bull" />
          )}
          {card.conflicting_evidence?.length > 0 && (
            <Group label="Conflicting evidence" items={card.conflicting_evidence} cls="text-tc-bear" icon={ShieldAlert} />
          )}
          <div className="text-[12px]">
            <span className="text-tc-muted">Risk geometry: </span>
            <span className="text-tc-txt">
              {rg.risk_reward != null ? `R:R ${rg.risk_reward}` : "conditional"}{rg.room_ok === false ? " · limited room" : rg.room_ok ? " · acceptable room" : ""}
            </span>
          </div>
          {card.why_you && (
            <div className="text-[12px] rounded-md bg-tc-mastery/10 border border-tc-mastery/25 px-2.5 py-1.5 text-tc-mastery">
              Why it matters to you: <b>{card.mapped_domain}</b> is currently one of your development areas.
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {card.can_open !== false && (
              <Action testid={`why-analyze-${sym}`} icon={Search} label="Analyze"
                onClick={() => { track("candidate_analyzed", { symbol: sym }); navigate(`/analyst?asset=${encodeURIComponent(sym)}`); }} />
            )}
            <Action testid={`why-explain-${sym}`} icon={MessageSquare} label="Explain"
              onClick={() => { track("explanation_requested", { symbol: sym }); navigate(`/co-analysis?asset=${encodeURIComponent(sym)}`); }} />
            <Action testid={`why-challenge-${sym}`} icon={Swords} label="Challenge"
              onClick={() => { track("challenge_tradecraft_started", { symbol: sym }); navigate(`/co-analysis?oversight=1&asset=${encodeURIComponent(sym)}`); }} />
            {card.why_you && (
              <Action testid={`why-practice-${sym}`} icon={GraduationCap} label="Practice this"
                onClick={() => { track("live_learning_started", { symbol: sym, domain: card.mapped_domain }); navigate("/learn"); }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-tc-muted">{label}</span>
      <span className="text-tc-txt font-mono">{value || "—"}</span>
    </div>
  );
}
function Group({ label, items, cls, icon: Icon = Target }) {
  return (
    <div className="text-[12px]">
      <div className="text-tc-muted mb-0.5">{label}</div>
      <ul className="space-y-0.5">
        {items.slice(0, 4).map((it, i) => (
          <li key={i} className={`flex items-start gap-1.5 ${cls}`}><Icon className="w-3 h-3 mt-0.5 shrink-0" /><span className="text-tc-sub">{it}</span></li>
        ))}
      </ul>
    </div>
  );
}
function Action({ testid, icon: Icon, label, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1 text-[12px] text-tc-sub hover:text-tc-coach hover:border-tc-coach/40 transition-colors">
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
